import { cap } from '../../../State'
import { translations } from '../../../translations'
const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

export const getProcessState = (conv) => {
  const value = conv?.processes?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))?.[0]
    ?.state
  if (conv.status === 'paused') return { label: t.inAttesaDiConferma, value }
  if (conv.status === 'canceled') return { label: t.cancellata, value }
  if (conv.status === 'ignored') return { label: t.ignorata, value }
  if (value === 3 && conv.status === 'ended') {
    if (conv.numeroTrasferta) return { label: t.trasfertaCreata, value }
    return { label: t.terminato, value }
  }
  if (value === 3 && conv.status === 'error') {
    if (!conv.noraIsIncluded) return { label: t.noraEsclusa, value } // Label di esclusione di Nora
    return { label: jsonErrors?.[conv.errorCode] || t.gestireDaMidoffice, value }
  }
  return { label: STATE_ENUM?.find((e) => e.value === value)?.label || t.inEsecuzione, value }
}

/**
 * Etichette degli stati di esecuzione **agentici**.
 *
 * Chiave: `execution.status` del DTO `ConversationExecution` che l'API ora allega a ogni
 * conversazione (`docs/nora/01-architettura.md` §7). Il mapping è quello previsto in
 * `docs/nora/07-interfacce.md` §2.2.
 *
 * `queued` e `running` condividono l'etichetta perché l'API **non li distingue**: la coda
 * vive nel database dell'Executor e la lista non lo interroga, per non trasformare
 * l'apertura della pagina in una richiesta HTTP per riga. Chi ha bisogno della differenza
 * apre il dettaglio.
 */
const AGENT_STATE_LABELS = {
  queued: t.inEsecuzione,
  running: t.inEsecuzione,
  waiting_user: t.agAttesaViaggiatore,
  waiting_confirmation: t.agPreventivoDaConfermare,
  waiting_operator: t.agAttesaOperatore,
  completed: t.terminato,
  error: t.agDaGestire
}

/**
 * Codici del filtro «stato» che valgono **solo** sulle conversazioni agentiche.
 * Tenuti separati da `USER_STATE_OPTIONS` perché i due insiemi non si sovrappongono: un
 * codice legacy applicato a una conversazione agentica non deve dare «tutte».
 */
export const AGENT_STATE_FILTERS = {
  AG_ATTESA_VIAGGIATORE: 'waiting_user',
  AG_DA_CONFERMARE: 'waiting_confirmation',
  AG_ATTESA_OPERATORE: 'waiting_operator',
  AG_DA_GESTIRE: 'error',
  AG_IN_ESECUZIONE: 'running',
  AG_TERMINATO: 'completed'
}

/**
 * Lo stato da mostrare in lista, per **qualunque** motore.
 *
 * Il difetto che chiude: `getProcessState` deriva lo stato dai `process`, che una
 * conversazione agentica non ha, e il fallback è «in esecuzione». Una conversazione ferma
 * in attesa del viaggiatore si presentava quindi come una che stava lavorando — cioè
 * esattamente il caso in cui la lista serve.
 *
 * Il motore si legge da `execution.engine`, che l'API deriva da `conversations.executionEngine`
 * e non dall'assenza di processi (difetto F5 di [07 §2.1]).
 *
 * @param {Object} conv - Conversazione, con `execution` se l'API la fornisce
 * @returns {{label: string, value: number|undefined, engine: string, executionStatus: string|undefined}}
 */
export const getExecutionState = (conv) => {
  const execution = conv?.execution
  if (execution?.engine !== 'agent') {
    // Nessun `execution`, o motore legacy: comportamento invariato. Il fallback vale
    // anche per un'API vecchia che il DTO non lo manda ancora.
    return { ...getProcessState(conv), engine: 'process_unit', executionStatus: execution?.status }
  }

  // Una conversazione agentica cancellata o ignorata resta cancellata o ignorata: sono
  // stati della conversazione, non dell'esecuzione, e l'operatore li riconosce così.
  if (conv.status === 'canceled') {
    return {
      label: t.cancellata,
      value: undefined,
      engine: 'agent',
      executionStatus: execution.status
    }
  }
  if (conv.status === 'ignored') {
    return {
      label: t.ignorata,
      value: undefined,
      engine: 'agent',
      executionStatus: execution.status
    }
  }

  return {
    label: AGENT_STATE_LABELS[execution.status] || t.inEsecuzione,
    value: undefined,
    engine: 'agent',
    executionStatus: execution.status,
    publicPhase: execution.publicPhase
  }
}

/**
 * La conversazione richiede l'attenzione di una persona **adesso**?
 *
 * È ciò che porta una riga in cima alla lista. Sul ramo agentico non si guardano i
 * processi: `waiting_operator` è un'escalation esplicita, `error` è una riconciliazione
 * che nessun retry può chiudere (consegna o operazione in `unknown`).
 *
 * @param {Object} conv - Conversazione, con `execution` se l'API la fornisce
 * @returns {boolean}
 */
export const hasOperatorWarning = (conv) => {
  if (conv?.execution?.engine === 'agent') {
    return ['waiting_operator', 'error'].includes(conv.execution.status)
  }
  const stato = conv?.processes?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))?.[0]
    ?.state
  return conv?.status !== 'error' && (conv?.status === 'paused' || stato === 5 || stato === 6)
}

export const STATE_ENUM = [
  { label: t.creato, value: 1, code: 'INITIAL', color: 'blue' },
  { label: t.inEsecuzione, value: 2, code: 'RUNNING', color: 'violet' },
  { label: t.terminato, value: 3, code: 'ENDED', color: 'green' },
  { label: t.errore, value: 5, code: 'ERROR', color: 'red' },
  { label: t.prontoARipartire, value: 7, code: 'READYTORESTART', color: 'orange' },
  { label: t.cancellata, value: 8, code: 'CANCELLED', color: 'yellow' }
]

/**
 * Opzioni stato visibili all'utente nella FilterBar, derivate dalla logica di getProcessState.
 * Ogni opzione corrisponde a uno stato "user-facing" (non allo stato DB).
 */
export const USER_STATE_OPTIONS = [
  { label: t.inAttesaDiConferma, code: 'IN_ATTESA' },
  { label: t.agAttesaViaggiatore, code: 'AG_ATTESA_VIAGGIATORE' },
  { label: t.agPreventivoDaConfermare, code: 'AG_DA_CONFERMARE' },
  { label: t.agAttesaOperatore, code: 'AG_ATTESA_OPERATORE' },
  { label: t.agDaGestire, code: 'AG_DA_GESTIRE' },
  { label: t.trasfertaCreata, code: 'TRASFERTA' },
  { label: t.cancellata, code: 'CANCELLATA' },
  { label: t.ignorata, code: 'IGNORATA' },
  { label: t.noraEsclusa, code: 'NORA_ESCLUSA' },
  { label: t.gestireDaMidoffice, code: 'GESTIRE_MIDOFFICE' },
  { label: t.inEsecuzione, code: 'IN_ESECUZIONE' },
  { label: t.terminato, code: 'TERMINATO' }
]

/**
 * Le opzioni di stato da mostrare: senza un canale agentico configurato negli environment
 * visibili, gli stati `AG_*` non possono trovare nulla e restano fuori dal dropdown.
 *
 * @param {boolean} agenticoAttivo - Esito di `getAgenticoAttivo`
 * @returns {Array<{label: string, code: string}>}
 */
export const opzioniStatoVisibili = (agenticoAttivo) =>
  agenticoAttivo
    ? USER_STATE_OPTIONS
    : USER_STATE_OPTIONS.filter((o) => !(o.code in AGENT_STATE_FILTERS))

export const getStateValues = () => {
  const obj = {}
  STATE_ENUM.forEach((e) => {
    obj[e.code] = e.value
  })
  return obj
}

export function getLabelDescriptionError(error) {
  return jsonErrors?.[error] || jsonErrors.default
}

/**
 * L'errore è uno dei motivi di passaggio all'operatore che la lista sa descrivere
 * (passeggeri multipli, allegati, profilo non trovato, …)? Gli altri codici — `genericError`,
 * `goelettSessionFailed` — non hanno una descrizione: `getLabelDescriptionError` li
 * ricondurrebbe a «Gestire da Midoffice», che sul codice `genericError` non dice cosa è successo.
 *
 * @param {string} [error] - `conversation.errorCode`
 * @returns {boolean}
 */
export function isKnownErrorCode(error) {
  return !!error && error !== 'default' && Object.prototype.hasOwnProperty.call(jsonErrors, error)
}

const jsonErrors = {
  passeggeriMultipli: t.passeggeriMultipli,
  attachmentsInEmail: t.attachmentsInEmail,
  nessunProfiloTrovato: t.nessunProfiloTrovato,
  noProfileInfoTrovato: t.noProfileInfoTrovato,
  nessunProfiloTrovatoTA: t.nessunProfiloTrovatoTA,
  profiloMittenteNonTA: t.profiloMittenteNonTA,
  duplicazioneProfiloTrovato: t.duplicazioneProfiloTrovato,
  richiestaModificaOCancellazioneTrasferta: t.richiestaModificaOCancellazioneTrasferta,
  richiestaAggiornamentoTrasferta: t.richiestaAggiornamentoTrasferta,
  outOfOffice: t.outOfOffice,
  sollecito: t.sollecito,
  maxAIMessagesReachedInGC: t.maxAIMessagesReachedInGC,
  default: t.gestireDaMidoffice
}

/**
 * Mappa dei colori CSS per ogni stato della conversazione.
 * Restituisce un colore hex basato sul codice colore dello stato.
 * @param {string} colorCode - Codice colore dallo STATE_ENUM (es. 'blue', 'green', 'red')
 * @returns {string} Colore CSS hex corrispondente allo stato
 */
const STATE_COLOR_MAP = {
  blue: '#2563eb',
  violet: '#8b5cf6',
  green: '#10b981',
  red: '#ef4444',
  orange: '#f59e0b',
  yellow: '#eab308'
}

/**
 * Restituisce il colore CSS associato a un determinato valore di stato.
 * @param {number} stateValue - Valore numerico dello stato dalla conversazione
 * @returns {string} Colore CSS hex. Ritorna grigio (#6b7280) se lo stato non è trovato
 */
export const getStateColor = (stateValue) => {
  const stateEntry = STATE_ENUM.find((e) => e.value === stateValue)
  return stateEntry ? STATE_COLOR_MAP[stateEntry.color] || '#6b7280' : '#6b7280'
}

/** Colore neutro per stati conclusi (canceled, ended senza trasferta, error gestireDaMidoffice) */
const NEUTRAL_COLOR = '#6b7280'

/** Colore verde per trasferta creata (ended con numeroTrasferta) */
const GREEN_COLOR = '#10b981'

/** Colore viola per stato ignored */
const VIOLET_COLOR = '#8b5cf6'

/** Colore rosso per nora esclusa (error con value 3 e !noraIsIncluded) */
const RED_COLOR = '#ef4444'

/** Colore giallo per stato in attesa di conferma (paused) */
const WARNING_COLOR = '#f59e0b'

/**
 * Restituisce il colore di visualizzazione per la badge stato in base alla conversazione.
 * - Verde: ended con numeroTrasferta (trasferta creata)
 * - Neutro: canceled, ended senza trasferta, error con value 3 e label gestireDaMidoffice (noraIsIncluded true)
 * - Viola: ignored
 * - Giallo: paused (in attesa di conferma)
 * - Altri: da getStateColor(value)
 * @param {Object} conv - Oggetto conversazione (o riga con status, processState, noraIsIncluded, numeroTrasferta)
 * @returns {string} Colore CSS hex
 */
export const getStateDisplayColor = (conv) => {
  if (!conv) return NEUTRAL_COLOR
  const status = conv.status
  const value =
    conv.processState?.value ??
    conv?.processes?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))?.[0]?.state

  if (status === 'paused') return WARNING_COLOR
  if (status === 'canceled') return NEUTRAL_COLOR
  if (status === 'ended' && conv.numeroTrasferta) return GREEN_COLOR
  if (status === 'ended') return NEUTRAL_COLOR
  if (status === 'ignored') return VIOLET_COLOR
  if (status === 'error' && value === 3 && !conv.noraIsIncluded) return RED_COLOR
  if (status === 'error' && value === 3 && conv.noraIsIncluded) return NEUTRAL_COLOR

  return getStateColor(value)
}

/**
 * Mappa delle icone PrimeIcons per ogni codice stato della conversazione.
 */
const STATE_ICON_MAP = {
  INITIAL: 'pi pi-circle',
  RUNNING: 'pi pi-spin pi-spinner',
  ENDED: 'pi pi-check-circle',
  ERROR: 'pi pi-times-circle',
  READYTORESTART: 'pi pi-refresh',
  CANCELLED: 'pi pi-ban'
}

/**
 * Restituisce la classe icona PrimeIcons associata a un determinato valore di stato.
 * @param {number} stateValue - Valore numerico dello stato dalla conversazione
 * @returns {string} Classe icona PrimeIcons. Ritorna 'pi pi-circle' se lo stato non è trovato
 */
export const getStateIcon = (stateValue) => {
  const stateEntry = STATE_ENUM.find((e) => e.value === stateValue)
  return stateEntry ? STATE_ICON_MAP[stateEntry.code] || 'pi pi-circle' : 'pi pi-circle'
}

/**
 * Mappa delle icone per ogni canale di comunicazione.
 */
const CHANNEL_ICON_MAP = {
  email: 'pi pi-envelope',
  whatsapp: 'pi pi-whatsapp',
  midoffice: 'pi pi-desktop',
  debug: 'pi pi-code'
}

/**
 * Restituisce la classe icona PrimeIcons per un canale di comunicazione.
 * @param {string} channel - Nome del canale di comunicazione (es. 'email', 'whatsapp')
 * @returns {string} Classe icona PrimeIcons. Ritorna 'pi pi-comment' come default
 */
export const getChannelIcon = (channel) => {
  return CHANNEL_ICON_MAP[channel] || 'pi pi-comment'
}

/**
 * Formatta le informazioni del viaggiatore in un oggetto strutturato
 * per una visualizzazione più leggibile nella tabella.
 * @param {Object} traveller - Oggetto viaggiatore dalla conversazione
 * @param {string} [traveller.name] - Nome del viaggiatore
 * @param {string} [traveller.surname] - Cognome del viaggiatore
 * @param {string} [traveller.email] - Email del viaggiatore
 * @param {string} [traveller.phone] - Telefono del viaggiatore
 * @param {string} [traveller.pid] - PID del viaggiatore
 * @returns {Object} Oggetto con fullName, email, phone, pid
 */
export const formatTravellerInfo = (traveller) => {
  if (!traveller) return { fullName: '-', email: '', phone: '', pid: '' }

  const fullName = [traveller.name, traveller.surname].filter(Boolean).join(' ') || '-'
  return {
    fullName,
    email: traveller.email || '',
    phone: traveller.phone || '',
    pid: traveller.pid || ''
  }
}
